import { useMemo } from 'react';
import {
  CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis,
} from 'recharts';
import { SectionCard } from '../yeni-design/primitives';
import { demoTheme } from '../../theme';
import { marjSeyri } from './analizHesaplari';
import { oranMetni } from './bicim';
import { BlokIskeleti } from './BlokIskeleti';
import { CUBUK_ETIKET, cubukBoyu } from './cubukBoyu';
import type { Donem, SatisDonemi } from './donem';
import type { SaKaynak } from './ekranKaynaklari';
import { sayi, type GunlukKarSatiri } from './ozetHesaplari';
import { useSaSorgu } from './useSatisVerisi';

interface Props {
  kaynak: SaKaynak | undefined;
  donem: Donem;
  donemTuru?: SatisDonemi;
  sube?: string | null;
  kanal?: string | null;
}

/** Karlilik : marj seyri (990052; kar / urun cirosu, kovada tutarlardan). Cizgi donem marjiyla kiyaslanir. */
export function MarjSeyirBlok({ kaynak, donem, donemTuru = 'ozel', sube = null, kanal = null }: Props) {
  const q = useSaSorgu<GunlukKarSatiri>(kaynak, { donem, sube, kanal }, !donem.tekGun);
  const boy = cubukBoyu(donemTuru, { bas: donem.bas, bit: donem.bit }).varsayilan;
  const satirlar = q.data?.data;
  const noktalar = useMemo(
    () => marjSeyri(satirlar ?? [], boy, { bas: donem.bas, bit: donem.bit }),
    [satirlar, boy, donem.bas, donem.bit],
  );

  if (!kaynak || donem.tekGun) return null;
  if (q.isLoading) return <BlokIskeleti testid="blok-marj-seyir-iskelet" baslik="Marj seyri" yukseklik={220} />;
  if (noktalar.length < 2) return null;

  const ciro = (satirlar ?? []).reduce((t, r) => t + sayi(r.urun_ciro), 0);
  const kar = (satirlar ?? []).reduce((t, r) => t + sayi(r.kar), 0);
  const donemMarji = ciro > 0 ? (kar / ciro) * 100 : null;

  return (
    <div data-testid="blok-marj-seyir">
      <SectionCard
        className="flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col"
        title="Marj seyri"
        subtitle={
          <>
            {CUBUK_ETIKET[boy]} bazında brüt kâr marjı · dönem{' '}
            <span data-testid="marj-seyir-donem">{donemMarji === null ? '-' : oranMetni(donemMarji)}</span>
          </>
        }
      >
        <div className="flex-1 min-h-[200px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={noktalar} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={demoTheme.border} vertical={false} />
              <XAxis
                dataKey="etiket"
                tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                axisLine={{ stroke: demoTheme.border }}
                tickLine={false}
                minTickGap={24}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={['auto', 'auto']}
                tickFormatter={(v) => oranMetni(Number(v), 0)}
                tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                axisLine={false}
                tickLine={false}
                width={48}
              />
              <ChartTooltip formatter={(v) => oranMetni(Number(v))} />
              <Line dataKey="marj" name="Marj" stroke={demoTheme.primary} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
              {donemMarji !== null && <ReferenceLine y={donemMarji} stroke={demoTheme.warning} strokeDasharray="4 4" />}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>
    </div>
  );
}
